import { useErrorHandlingLazyQuery } from '@backoffice-frontend/data-fetching'
import type { WorkOrderServiceItem } from '@backoffice-frontend/graphql'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { useEffect } from 'react'
import {
  type Control,
  type FieldPathByValue,
  type FieldValues,
  type UseFormTrigger,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { opsCareCategoryRights } from './utils'
import type { WorkOrderAssigneeOption } from './WorkOrderAssigneeField'
import { useWorkOrderAssigneesByRightsLazyQuery } from './WorkOrderAssigneesByRights.hook'

const OPSCARE_LOGIN = BoumRight.APP_OPSCARE_LOGIN.str()

/**
 * Returns the `rules` for `WorkOrderAssigneeField`: they fail when the selected
 * assignee uses OpsCare but cannot see work orders of the form's service item
 * there. Assignees without OpsCare login work on the work order in the
 * Backoffice, so they are accepted.
 *
 * The check also runs when the form opens and whenever the service item or the
 * assignee changes, as forms that validate on blur or on submit would not
 * re-validate the assignee on their own.
 *
 * Without `BACKOFFICE_USER_RIGHTS_R` and `BACKOFFICE_USER_USER_R` the check
 * cannot run. The field is then disabled, so the assignee cannot be changed and
 * is accepted as it is.
 */
export const useAssigneeAccessRules = <FormValues extends FieldValues>({
  control,
  trigger,
  assigneeName,
  serviceItemOf,
}: {
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  assigneeName: FieldPathByValue<FormValues, WorkOrderAssigneeOption | null>
  serviceItemOf: (
    values: FormValues,
  ) => WorkOrderServiceItem | '' | null | undefined
}) => {
  const { t } = useTranslation()
  const [fetchUsersByRights] = useErrorHandlingLazyQuery(
    useWorkOrderAssigneesByRightsLazyQuery,
    { onError: () => {} },
  )
  const canLookUpAssignees = useUserHasAccess({
    requiredRights: [
      // To get the list of users.
      BoumRight.BACKOFFICE_USER_USER_R,
      // Guard related to the query itself: `backofficeUsersByRights`.
      BoumRight.BACKOFFICE_USER_RIGHTS_R,
    ],
  })

  const serviceItem = useWatch({ control, compute: serviceItemOf })
  const selectedAssignee = useWatch({ control, name: assigneeName })

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check when the service item or the assignee changes
  useEffect(() => {
    void trigger(assigneeName)
  }, [serviceItem, selectedAssignee, assigneeName, trigger])

  // Must stay an async function: react-hook-form only tracks `isValidating`
  // for validators it recognises as async.
  const validate = async (
    assignee: WorkOrderAssigneeOption | null,
    values: FormValues,
  ): Promise<true | string> => {
    const categoryRights = opsCareCategoryRights(serviceItemOf(values))
    // Every OpsCare user sees service items without a category right
    if (!assignee || !canLookUpAssignees || categoryRights.length === 0) {
      return true
    }

    const holdsRights = async (rights: string[]) => {
      const { data, error } = await fetchUsersByRights({
        variables: { rights },
      })
      if (error) throw error
      return Boolean(
        data?.backofficeUsersByRights?.some(user => user.id === assignee.id),
      )
    }

    try {
      if (await holdsRights([OPSCARE_LOGIN, ...categoryRights])) return true
      return (
        !(await holdsRights([OPSCARE_LOGIN])) ||
        t('workOrdersComponents_assigneeField_missingOpsCareAccess_error')
      )
    } catch (error) {
      console.error('Failed to check the OpsCare access of the assignee', error)
      return t('workOrdersComponents_assigneeField_accessCheckFailed_error')
    }
  }

  return { validate }
}
